/* import __COLOCATED_TEMPLATE__ from './spam.hbs'; */
/* RESPONSIBLE TEAM: team-channels */
import Component from '@glimmer/component';
import { SpamResource } from 'embercom/components/inbox2/spam-resource';
import { inject as service } from '@ember/service';
import type InboxState from 'embercom/services/inbox-state';
import type Inbox2Counters from 'embercom/services/inbox2-counters';
import type Snackbar from 'embercom/services/snackbar';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { type SpamMessage } from 'embercom/objects/inbox/spam';
import { taskFor } from 'ember-concurrency-ts';
import Spam from 'embercom/objects/inbox/inboxes/spam';
import { debounce } from '@ember/runloop';
import moment from 'moment-timezone';

const SPAM_HISTORY_LIMIT_DAYS = 60;

const ALL_COLUMNS = [
  {
    label: 'name',
    valuePath: 'name',
    width: '220px',
  },
  {
    label: 'email',
    valuePath: 'email',
    width: '270px',
  },
  {
    label: 'subject',
    valuePath: 'emailSubject',
    width: '350px',
  },
  {
    label: 'date',
    valuePath: 'createdAt',
    width: '150px',
  },
  {
    label: 'rejection_reason',
    valuePath: 'rejectionReasonLabel',
    width: '200px',
  },
];

interface Args {}

interface Signature {
  Args: Args;
}

export default class SpamComponent extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare inbox2Counters: Inbox2Counters;
  @service declare snackbar: Snackbar;
  @service declare intl: IntlService;
  @service declare intercomConfirmService: any;

  @tracked messageToPreview: SpamMessage | undefined;
  @tracked processingMessage: SpamMessage | undefined;
  @tracked currentlyOpenEmailId: number | undefined;
  @tracked emailFilter = '';
  @tracked startDate: Date | null = null;
  @tracked endDate: Date | null = null;
  @tracked hoveredMessageId: number | undefined;
  @tracked selectedMessages: Set<number> = new Set();
  @tracked messagesToDelete: number[] = [];
  @tracked isDeletingSelected = false;
  @tracked showDeleteConfirmationModal = false;
  @tracked showWhitelistModal = false;
  @tracked whitelistModalResolve:
    | ((value: { shouldWhitelist: boolean; dontAskAgain: boolean }) => void)
    | null = null;

  spamInbox = new Spam(0); // The count does not matter here as we are just using it to generate a key for the Inbox counters service

  columns = ALL_COLUMNS;

  readonly spamResource = SpamResource.from(this);

  // LocalStorage key for "don't ask again" preference
  private readonly DONT_ASK_WHITELIST_KEY = 'spam-dont-ask-whitelist';

  get trustedEmailsSet(): Set<string> {
    return new Set(this.spamResource.trustedEmails);
  }

  get shouldSkipWhitelistModal(): boolean {
    try {
      return localStorage.getItem(this.DONT_ASK_WHITELIST_KEY) === 'true';
    } catch (e) {
      // If localStorage is not available, don't skip
      return false;
    }
  }

  private setDontAskAgainPreference(value: boolean) {
    try {
      if (value) {
        localStorage.setItem(this.DONT_ASK_WHITELIST_KEY, 'true');
      } else {
        localStorage.removeItem(this.DONT_ASK_WHITELIST_KEY);
      }
    } catch (e) {
      console.error(
        'Error setting dont ask again preference due to localStorage not being available:',
        e,
      );
    }
  }

  get startDateAsMoment() {
    return this.startDate ? moment(this.startDate) : null;
  }

  get endDateAsMoment() {
    return this.endDate ? moment(this.endDate) : null;
  }

  get startDateForAPI() {
    if (!this.startDate) {
      return undefined;
    }
    let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return moment(this.startDate).tz(userTimezone).startOf('day').format();
  }

  get endDateForAPI() {
    if (!this.endDate) {
      return undefined;
    }
    let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return moment(this.endDate).tz(userTimezone).endOf('day').format();
  }

  get spamHistoryLimitDate() {
    return moment().subtract(SPAM_HISTORY_LIMIT_DAYS, 'days').toDate();
  }

  get todaysDate() {
    return moment().toDate();
  }

  @action openPreviewPanel(spamMessage: SpamMessage) {
    this.messageToPreview = spamMessage;
    this.currentlyOpenEmailId = spamMessage.id;
  }

  @action hidePreviewPanel() {
    this.messageToPreview = undefined;
    this.currentlyOpenEmailId = undefined;
  }

  get shouldHaveVisibleCheckbox() {
    return (spamMessageId: number): boolean => {
      return this.hoveredMessageId === spamMessageId || this.selectedMessages.size > 0;
    };
  }

  @action toggleSelection(spamMessage: SpamMessage, event: Event) {
    event.stopPropagation();

    if (!spamMessage?.id) {
      return;
    }

    let newSelectedMessages = new Set(this.selectedMessages);
    if (newSelectedMessages.has(spamMessage.id)) {
      newSelectedMessages.delete(spamMessage.id);
    } else {
      newSelectedMessages.add(spamMessage.id);
    }
    this.selectedMessages = newSelectedMessages;
  }

  get isMessageSelected() {
    return (spamMessageId: number): boolean => {
      return this.selectedMessages.has(spamMessageId);
    };
  }

  get hasSelectedMessages() {
    return this.selectedMessages.size > 0;
  }

  get isAllSelected() {
    return (
      this.spamResource.spamMessages.length > 0 &&
      this.spamResource.spamMessages.every((message) => this.selectedMessages.has(message.id))
    );
  }

  get isPartiallySelected() {
    return this.selectedMessages.size > 0 && !this.isAllSelected;
  }

  @action selectAll() {
    let allMessageIds = new Set(this.spamResource.spamMessages.map((message) => message.id));
    this.selectedMessages = allMessageIds;
  }

  @action deselectAll() {
    this.selectedMessages = new Set();
  }

  @action toggleSelectAll() {
    if (this.isAllSelected) {
      this.selectedMessages = new Set();
    } else {
      this.selectedMessages = new Set(this.spamResource.spamMessages.map((message) => message.id));
    }
  }

  @action async markAsNotSpam(spamMessage: SpamMessage, event: Event) {
    event.stopPropagation();

    if (spamMessage?.id) {
      let shouldWhitelist = false;

      if (this.trustedEmailsSet.has(spamMessage.email)) {
        // Check if email is already trusted
        shouldWhitelist = false;
      } else if (this.shouldSkipWhitelistModal) {
        // Check if user has chosen "don't ask me again"
        shouldWhitelist = false;
      } else {
        // Show confirmation modal
        let result = await this.showWhitelistConfirmation();
        shouldWhitelist = result.shouldWhitelist;

        // If user chose "don't ask again", store preference
        if (result.dontAskAgain) {
          this.setDontAskAgainPreference(true);
        }

        // If user chose to whitelist, optimistically add to our trusted emails list
        if (shouldWhitelist) {
          this.spamResource.trustedEmails = [...this.spamResource.trustedEmails, spamMessage.email];
        }
      }

      this.processingMessage = spamMessage;

      await taskFor(this.spamResource.preloadItemsForDeletion).perform(1);

      let messageProcessed = await taskFor(this.spamResource.markAsNotSpam).perform(
        spamMessage.id,
        shouldWhitelist,
      );

      if (messageProcessed) {
        this.spamResource.spamMessages.removeObject(spamMessage);
        this.messageToPreview = undefined;
        this.inbox2Counters.decrementCount(this.spamInbox);
      } else if (shouldWhitelist) {
        // If processing failed and we optimistically added to trusted list, remove it
        // It maybe possible that email was successfully whitelisted, but something went wrong
        // with the "mark as not spam" API call. We still remove it from our local trusted emails list.
        // At worst, it will show the modal again for an email already added to trusted emails list
        // But the backend will take care of it. So it's fine and safe!
        this.spamResource.trustedEmails = this.spamResource.trustedEmails.filter(
          (email) => email !== spamMessage.email,
        );
      }
      this.processingMessage = undefined;
    }
  }

  @action showDeleteConfirmationModalForMessage(spamMessage: SpamMessage, event: Event) {
    event.stopPropagation();
    if (spamMessage?.id) {
      this.messagesToDelete = [spamMessage.id];
      this.showDeleteConfirmationModal = true;
    }
  }

  @action openDeleteConfirmationModal() {
    if (this.selectedMessages.size === 0) {
      return;
    }
    this.messagesToDelete = Array.from(this.selectedMessages);
    this.showDeleteConfirmationModal = true;
  }

  @action hideDeleteConfirmationModal() {
    this.showDeleteConfirmationModal = false;
    this.messagesToDelete = [];
  }

  @action handleRowClick(spamMessage: SpamMessage, event: Event) {
    let target = event.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input[type="checkbox"]') ||
      target.closest('label')
    ) {
      return;
    }

    if (this.currentlyOpenEmailId === spamMessage.id) {
      this.hidePreviewPanel();
    } else {
      this.openPreviewPanel(spamMessage);
    }
  }

  @action async confirmDeleteSelectedMessages() {
    this.showDeleteConfirmationModal = false;

    if (this.messagesToDelete.length === 0) {
      return;
    }

    this.isDeletingSelected = true;

    try {
      await taskFor(this.spamResource.preloadItemsForDeletion).perform(
        this.messagesToDelete.length,
      );

      let messagesToDeleteSet = new Set(this.messagesToDelete);
      let messages = this.spamResource.spamMessages.filter((msg) =>
        messagesToDeleteSet.has(msg.id),
      );

      let success = await taskFor(this.spamResource.deleteSpamMessages).perform(
        this.messagesToDelete,
        true,
      );

      if (success) {
        messages.forEach((message) => {
          this.spamResource.spamMessages.removeObject(message);
          this.inbox2Counters.decrementCount(this.spamInbox);
        });

        if (this.messageToPreview && messagesToDeleteSet.has(this.messageToPreview.id)) {
          this.messageToPreview = undefined;
        }

        let newSelectedMessages = new Set(this.selectedMessages);
        this.messagesToDelete.forEach((id) => newSelectedMessages.delete(id));
        this.selectedMessages = newSelectedMessages;

        if (messages.length === 1) {
          this.snackbar.notify(this.intl.t('inbox.spam.confirmation-modal.delete-success-single'), {
            type: 'confirmation',
          });
        } else {
          this.snackbar.notify(
            this.intl.t('inbox.spam.confirmation-modal.delete-success-multiple', {
              count: messages.length,
            }),
            {
              type: 'confirmation',
            },
          );
        }
      } else {
        this.snackbar.notify(
          this.intl.t('inbox.spam.confirmation-modal.delete-error', {
            count: messages.length,
          }),
          {
            type: 'error',
          },
        );
      }
    } finally {
      this.isDeletingSelected = false;
      this.messagesToDelete = [];
    }
  }

  private showWhitelistConfirmation(): Promise<{
    shouldWhitelist: boolean;
    dontAskAgain: boolean;
  }> {
    return new Promise((resolve) => {
      this.whitelistModalResolve = resolve;
      this.showWhitelistModal = true;
    });
  }

  @action handleWhitelistModalConfirm(shouldWhitelist: boolean, dontAskAgain: boolean) {
    if (this.whitelistModalResolve) {
      this.whitelistModalResolve({ shouldWhitelist, dontAskAgain });
      this.whitelistModalResolve = null;
    }
  }

  @action closeWhitelistModal() {
    this.showWhitelistModal = false;
  }

  @action updateEmailFilter(event: Event) {
    let target = event.target as HTMLInputElement;
    let value = target.value;

    debounce(this, 'applyEmailFilter', value, 300);
  }

  @action applyEmailFilter(value: string) {
    this.emailFilter = value;
    this.applyFilters();
  }

  @action clearEmailFilter() {
    this.emailFilter = '';
    this.applyFilters();
  }

  @action setStartDate(datePickerResult: any) {
    if (datePickerResult && datePickerResult.moment) {
      this.startDate = datePickerResult.moment.toDate();

      // If no end date is set, automatically set end date to today
      if (!this.endDate) {
        this.endDate = this.todaysDate;
      }
    } else {
      this.startDate = null;
    }
    this.applyFilters();
  }

  @action setEndDate(datePickerResult: any) {
    if (datePickerResult && datePickerResult.moment) {
      this.endDate = datePickerResult.moment.toDate();

      // If no start date is set, automatically set start date to spam history limit
      if (!this.startDate) {
        this.startDate = moment().subtract(SPAM_HISTORY_LIMIT_DAYS, 'days').toDate();
      }
    } else {
      this.endDate = null;
    }
    this.applyFilters();
  }

  @action clearStartDate() {
    this.startDate = null;
    this.applyFilters();
  }

  @action clearEndDate() {
    this.endDate = null;
    this.applyFilters();
  }

  @action clearDateFilters() {
    this.startDate = null;
    this.endDate = null;
    this.applyFilters();
  }

  @action clearAllFilters() {
    this.emailFilter = '';
    this.startDate = null;
    this.endDate = null;
    this.applyFilters();
  }

  private applyFilters() {
    this.spamResource.applyFilters({
      from_email: this.emailFilter,
      start_date: this.startDateForAPI,
      end_date: this.endDateForAPI,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Spam': typeof SpamComponent;
    'inbox2/spam': typeof SpamComponent;
  }
}
